import { NgClass } from '@angular/common';
import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  SiaComponentConfigPerspectives,
  SiaComponentConfigService,
  SiaI18nDirective,
  SiaI18nService,
  SiaSizeDetectorService,
  SiaTranslatePipe,
  SiaUsersService,
  SiaWindowSize,
  notify,
} from '@sia/shared';
import { Subscription } from 'rxjs/internal/Subscription';
import { GridConfigClass } from '../../interfaces/grid-config';
import { GridConfigService } from '../../services/grid-config.service';
import { SiaBaseComponent } from '../sia-base/sia-base.component';
import { SiaGridConfigActionsComponent } from '../sia-grid-config-actions/sia-grid-config-actions.component';
import { SiaGridConfigAdaptabilityComponent } from '../sia-grid-config-adaptability/sia-grid-config-adaptability.component';
import { SiaGridConfigAdvancedComponent } from '../sia-grid-config-advanced/sia-grid-config-advanced.component';
import { SiaGridConfigBaseComponent } from '../sia-grid-config-base/sia-grid-config-base.component';
import { SiaGridConfigColsPartOneComponent } from '../sia-grid-config-cols-part-one/sia-grid-config-cols-part-one.component';
import { SiaGridConfigColsPartThreeComponent } from '../sia-grid-config-cols-part-three/sia-grid-config-cols-part-three.component';
import { SiaGridConfigColsPartTwoComponent } from '../sia-grid-config-cols-part-two/sia-grid-config-cols-part-two.component';
import { SiaGridConfigDetailComponent } from '../sia-grid-config-detail/sia-grid-config-detail.component';
import { SiaGridConfigFiltersComponent } from '../sia-grid-config-filters/sia-grid-config-filters.component';
import { SiaGridConfigSetupComponent } from '../sia-grid-config-setup/sia-grid-config-setup.component';
import { SiaGridConfigSpParametersComponent } from '../sia-grid-config-sp-parameters/sia-grid-config-sp-parameters.component';
import { SiaGridConfigStyleComponent } from '../sia-grid-config-style/sia-grid-config-style.component';
import { SiaGridConfigTab } from '../sia-grid-config-tab/sia-grid-config-tab.component';
import { SiaGridConfigValidationComponent } from '../sia-grid-config-validation/sia-grid-config-validation.component';

@Component({
  selector: 'sia-grid-config',
  templateUrl: './sia-grid-config.component.html',
  styleUrls: ['./sia-grid-config.component.scss'],
  standalone: true,
  imports: [
    SiaI18nDirective,
    NgClass,
    SiaGridConfigSetupComponent,
    SiaGridConfigBaseComponent,
    SiaGridConfigAdvancedComponent,
    SiaGridConfigAdaptabilityComponent,
    SiaGridConfigColsPartOneComponent,
    SiaGridConfigColsPartTwoComponent,
    SiaGridConfigColsPartThreeComponent,
    SiaGridConfigActionsComponent,
    SiaGridConfigValidationComponent,
    SiaGridConfigDetailComponent,
    SiaGridConfigFiltersComponent,
    SiaGridConfigSpParametersComponent,
    SiaGridConfigStyleComponent,
    SiaTranslatePipe,
  ],
})
export class SiaGridConfigComponent
  extends SiaBaseComponent
  implements OnDestroy, OnInit
{
  private i18nService = inject(SiaI18nService);
  private usersService = inject(SiaUsersService);
  private componentConfigService = inject(SiaComponentConfigService);
  private sizeDetectorService = inject(SiaSizeDetectorService);
  private gridConfig = inject(GridConfigService);

  @ViewChild(SiaGridConfigTab, { static: false })
  configTab: SiaGridConfigTab | undefined;

  private _currentTab: string = 'setup';
  private _tempGridConf: string = '';
  private _gridConf: GridConfigClass | null = new GridConfigClass();
  private _tempCols: any[] = [];
  private _isDesktop: boolean = false;
  isAdmin = false;
  confVisible = false;
  private _componentPerspective: SiaComponentConfigPerspectives = {};
  private _subs: Subscription[] = [];
  windowSize: SiaWindowSize = {};
  @HostListener('window:unload')
  ngOnDestroy() {
    this._currentTab = '';
    this._tempGridConf = '';
    this._gridConf = null;
    this._tempCols = [];
    this._subs.forEach((sub) => {
      sub.unsubscribe();
    });
    super.ngOnDestroy();
  }

  get currentTab() {
    return this._currentTab;
  }

  get gridConf() {
    return this._gridConf;
  }

  get tempCols() {
    return this._tempCols;
  }

  ngOnInit() {
    let user = this.usersService.getUserData();
    let s0 = this.sizeDetectorService.onResize$.subscribe(
      (size: SiaWindowSize | null) => {
        if (size) {
          this.windowSize = size;
        }
      },
    );
    this._subs.push(s0);
    if (!user) {
      this.isAdmin = false;
      return;
    }
    this.isAdmin = user.isAdmin ?? false;
    let s1 = this.sizeDetectorService.onResize$.subscribe(
      (size: SiaWindowSize | null) => {
        if (size) {
          this._isDesktop = !size.isMobile;
        }
      },
    );
    this._subs.push(s1);
  }

  isDesktop() {
    return this._isDesktop;
  }
  refreshCurrentConfiguration(event: any) {
    this.gridConfig
      .refreshGridConfiguration(this._componentPerspective.id ?? '')
      .then((value: any) => {
        if (value) {
          this._gridConf = value;
          notify('Aggiornate colonne correttamente', 3000, 'success');
        } else {
          notify('Impossibile aggiornare colonne', 3000, 'error');
        }
      });
  }
  saveDataConfig() {
    if (this.configTab) {
      this.configTab.saveData();
    }
  }

  updateRemoteConf() {
    this.saveDataConfig();
    // this.checkReportAction();
    let conf = this._gridConf;

    let g = JSON.stringify(conf);
    if (g === this._tempGridConf) {
      notify('Nessuna modifica da salvare', 1500, 'warning');
    } else {
      this._componentPerspective.jsonConfig = JSON.stringify(this._gridConf);
      this._tempGridConf = JSON.stringify(this._gridConf);
      this._tempCols = this._gridConf?.cols?.map((x) => x.dataField) ?? [];
      this._tempCols = this._tempCols.concat(
        this._gridConf?.actions.map((x) => x.id),
      );
      if (this._componentPerspective.id) {
        this.componentConfigService
          .updateComponentPerspective(this._componentPerspective)
          .then((res: any) => {
            notify('Configurazione salvata con successo', 2000, 'success');
          });
      } else {
        this.componentConfigService
          .setComponentPerspective(this._componentPerspective)
          .then((res: SiaComponentConfigPerspectives) => {
            this._componentPerspective = res;
            notify('Configurazione salvata con successo', 2000, 'success');
          });
      }
    }
  }
  onNavItemClick(itemId: string) {
    this.saveDataConfig();
    this._currentTab = itemId;
  }

  onPerspectiveSelected(event: SiaComponentConfigPerspectives) {
    this._componentPerspective = event;
    if (event == null || Object.keys(event).length == 0) return;
    if (!event.id) {
      let conf = JSON.parse(event.jsonConfig ?? '');
      let realConf: GridConfigClass = new GridConfigClass();
      Object.assign(realConf, conf);
      this._gridConf = realConf;
      this._tempGridConf = JSON.stringify(this._gridConf);
      this._tempCols = this._gridConf.cols?.map((x) => x.dataField) ?? [];
      this._tempCols = this._tempCols.concat(
        this._gridConf?.actions.map((x) => x.id),
      );
    } else {
      if (!event.jsonConfig) return;
      this._gridConf = JSON.parse(event.jsonConfig);
      if (!this._gridConf) return;
      this._tempGridConf = JSON.stringify(this._gridConf);
      this._tempCols = this._gridConf.cols?.map((x) => x.dataField) ?? [];
      this._tempCols = this._tempCols.concat(
        this._gridConf?.actions.map((x) => x.id),
      );
    }
  }

  onViewConfiguration() {
    this.confVisible = true;
    this._currentTab = 'impostazioni-base';
  }

  onCloneConfiguration(event: any) {
    this.componentConfigService
      .clonePerspective(event.perspectiveId, event.cloneFrom)
      .then((res: SiaComponentConfigPerspectives) => {
        if (res) {
          notify('Configurazione clonata con successo', 2000, 'success');
          this.confVisible = false;
        }
      });
  }
}
